import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Reveal, Eyebrow } from "@/components/Reveal";
import CtaBand from "@/components/home/CtaBand";
import { API } from "@/data/content";

const fmt = (n, dp) => n.toLocaleString("en-AU", { minimumFractionDigits: dp, maximumFractionDigits: dp });

const GROUP_META = {
  indices: { title: "Indices", dp: 2, note: "" },
  metals: { title: "Metals", dp: 2, note: "USD/oz" },
  crypto: { title: "Crypto", dp: 0, note: "USD" },
  fx: { title: "AUD exchange rates", dp: 4, note: "per A$1" },
};
const GROUP_ORDER = ["indices", "metals", "crypto", "fx"];

function SparkCard({ item, dp, note }) {
  const [points, setPoints] = useState(null);
  const up = item.change_pct >= 0;
  const trendUp = points && points.length > 1 ? points[points.length - 1].c >= points[0].c : up;
  const stroke = trendUp ? "#1B8844" : "#DC2626";

  useEffect(() => {
    let alive = true;
    fetch(`${API}/markets/history?symbol=${encodeURIComponent(item.symbol)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && d && setPoints(d.points))
      .catch(() => {});
    return () => { alive = false; };
  }, [item.symbol]);

  const monthChange = points && points.length > 1
    ? ((points[points.length - 1].c - points[0].c) / points[0].c) * 100
    : null;

  return (
    <div
      data-testid={`markets-card-${item.symbol.replace(/\W+/g, "-").toLowerCase()}`}
      className="rounded-3xl bg-white border border-ink/8 p-6 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/60 hover:shadow-lift"
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink/40 font-semibold">{item.name}</div>
          <div className="font-display text-2xl font-bold text-ink mt-1">
            {fmt(item.price, dp)}
            {note && <span className="text-[10px] text-ink/35 font-normal ml-1.5">{note}</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 ${up ? "bg-brand-light text-brand-dark" : "bg-red-50 text-red-600"}`}>
            {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {up ? "+" : ""}{item.change_pct.toFixed(2)}%
          </span>
          {monthChange !== null && (
            <div className={`text-[10px] mt-1.5 font-semibold ${monthChange >= 0 ? "text-brand" : "text-red-500"}`} data-testid={`markets-30d-${item.symbol.replace(/\W+/g, "-").toLowerCase()}`}>
              {monthChange >= 0 ? "+" : ""}{monthChange.toFixed(1)}% · 30d
            </div>
          )}
        </div>
      </div>

      <div className="h-24 -mx-2 mt-2">
        {points ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <defs>
                <linearGradient id={`g-${item.symbol.replace(/\W/g, "")}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" hide />
              <YAxis hide domain={["dataMin", "dataMax"]} />
              <Tooltip
                formatter={(v) => [fmt(v, dp), item.name]}
                labelFormatter={(t) => new Date(t).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                contentStyle={{ borderRadius: 12, border: "1px solid rgba(15,23,42,0.08)", fontSize: 12 }}
              />
              <Area type="monotone" dataKey="c" stroke={stroke} strokeWidth={2} fill={`url(#g-${item.symbol.replace(/\W/g, "")})`} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full rounded-xl bg-ink/[0.04] animate-pulse" />
        )}
      </div>
    </div>
  );
}

export default function Markets() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch(`${API}/markets`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setFailed(true));
  }, []);

  return (
    <div data-testid="markets-page">
      <section className="pt-36 sm:pt-44 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Live markets</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink leading-[1.05] max-w-3xl">
              The numbers behind <span className="font-editorial italic font-medium text-gold-600">the conversation.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-ink/60 max-w-2xl leading-relaxed">
              Indices, metals, crypto and AUD exchange rates — with 30-day charts. Indicative data, refreshed hourly,
              may be delayed. Not financial advice.
            </p>
            {data && (
              <p className="mt-3 text-xs text-ink/40" data-testid="markets-updated">
                Last updated {new Date(data.updated_at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {failed && (
            <p className="text-sm text-ink/50" data-testid="markets-error">Market data is temporarily unavailable — please check back shortly.</p>
          )}
          {!data && !failed && <div className="h-40 rounded-3xl bg-ink/[0.04] animate-pulse" data-testid="markets-loading" />}
          {data && GROUP_ORDER.map((key) => {
            const items = data.groups?.[key] || [];
            if (!items.length) return null;
            const meta = GROUP_META[key];
            return (
              <div key={key} data-testid={`markets-group-${key}`}>
                <h2 className="font-display text-xl sm:text-2xl font-bold text-ink mb-6 flex items-center gap-3">
                  <span className="h-px w-8 bg-gold" />{meta.title}
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {items.map((m) => <SparkCard key={m.symbol} item={m} dp={meta.dp} note={meta.note} />)}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
